import {PI_CARDINAL} from '../pi/piPaths';
import type {PlushState} from './PaiPlush';
import {
  KIT_CHEEK,
  KnitBeanie,
  PlushFace,
  PlushFrame,
  type FurStops,
} from './plushKit';

/**
 * Plush π: the letter π sewn as a pillowy felt plush. A stuffed bar with
 * a beady face, two stuffed legs (the right one curling out like the
 * hand-drawn glyph's), and visible stitched seams. Comes in cream or
 * cardinal felt, and can borrow Beanie's hat.
 */

/** The felt colorways. */
export type PlushPiPalette = 'cream' | 'cardinal';

const PALETTES: Record<
  PlushPiPalette,
  {fur: FurStops; stitch: string; cheek: string; hat: string}
> = {
  cream: {
    fur: {light: '#FFF9EE', mid: '#F6E9D3', dark: '#E6D0AE'},
    stitch: PI_CARDINAL,
    cheek: KIT_CHEEK,
    hat: PI_CARDINAL,
  },
  cardinal: {
    fur: {light: '#E0544E', mid: '#BF2F2B', dark: '#8C1515'},
    stitch: '#FFE9DC',
    cheek: '#FF9AA8',
    hat: '#F6E9D3',
  },
};

/** Stuffed-bar outline. */
const BAR =
  'M 22 74 C 22 54 36 46 56 46 L 150 46 C 170 46 180 56 178 74 ' +
  'C 176 92 166 100 148 100 L 52 100 C 34 100 22 92 22 74 Z';

/** Seam stitched just inside the bar's outline. */
const BAR_SEAM =
  'M 31 74 C 31 60 42 54 58 54 L 148 54 C 164 54 171 61 170 74 ' +
  'C 169 86 162 92 148 92 L 54 92 C 40 92 31 86 31 74 Z';

const LEFT_LEG = 'M 76 96 C 76 136 72 164 56 190';
const RIGHT_LEG =
  'M 124 96 C 124 146 126 172 146 184 C 156 190 166 184 170 174';

/** Props for {@link PlushPi}. */
export interface PlushPiProps {
  /** Rendered width in px. */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
  /** Felt colorway. Omitted defaults to `cream`. */
  palette?: PlushPiPalette;
  /** Puts Beanie's knit hat on it. Omitted means no hat. */
  hasBeanie?: boolean;
}

/**
 * Renders Plush π (see module comment). Decorative (`aria-hidden`).
 *
 * @param props - See {@link PlushPiProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function PlushPi({
  size,
  state = 'idle',
  palette = 'cream',
  hasBeanie = false,
}: PlushPiProps) {
  const colors = PALETTES[palette];
  const seam = {
    stroke: colors.stitch,
    strokeWidth: 2.2,
    strokeDasharray: '5 5',
    strokeLinecap: 'round' as const,
    fill: 'none',
    opacity: 0.75,
  };

  return (
    <PlushFrame
      size={size}
      state={state}
      furStops={colors.fur}
      eyeLineFraction={70 / 220}
      thoughtAnchor={{x: 164, y: hasBeanie ? 20 : 40}}
    >
      {kit => (
        <>
          <g filter={kit.fuzz}>
            <path
              d={LEFT_LEG}
              stroke={kit.fur}
              strokeWidth={30}
              strokeLinecap="round"
              fill="none"
            />
            <path
              d={RIGHT_LEG}
              stroke={kit.fur}
              strokeWidth={30}
              strokeLinecap="round"
              fill="none"
            />
          </g>
          <path d={LEFT_LEG} {...seam} />
          <path d={RIGHT_LEG} {...seam} />
          <g filter={kit.fuzz}>
            <path d={BAR} fill={kit.fur} />
          </g>
          <path d={BAR_SEAM} {...seam} />
          {hasBeanie && (
            <KnitBeanie kit={kit} color={colors.hat} dy={-20} scale={0.72} />
          )}
          <PlushFace
            kit={kit}
            x={100}
            y={70}
            scale={0.82}
            cheek={colors.cheek}
          />
        </>
      )}
    </PlushFrame>
  );
}
